import { headingText } from '../../utils/headingText';
import { useState } from "react";
import SEO from '../SEO';
import Image from "../../assets/Photos/website-phone-graphic.svg";
import {
    FaLinkedin,
    FaEnvelope,
    FaInstagram,
} from "react-icons/fa";

export default function ContactHero() {
    const [status, setStatus] = useState("idle");

    const handleSubmit = async (event) => {
        event.preventDefault();

        const form = event.currentTarget;
        const email = form.email.value.trim();

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            setStatus("invalid-email");
            return;
        }

        setStatus("submitting");

        const formData = new FormData(form);

        try {
            const response = await fetch("https://formspree.io/f/mvkpvvao", {
                method: "POST",
                body: formData,
                headers: {
                    Accept: "application/json",
                },
            });

            if (response.ok) {
                form.reset();
                setStatus("success");
            } else {
                setStatus("error");
            }
        } catch (error) {
            setStatus("error");
        }
    };

    return (
        <section className="w-full overflow-x-hidden">
            <SEO title="Contact a Nonprofit Marketing & Technology Consultant" path="/contact" description="Discuss nonprofit website design, communications, CRM, or practical technology in a free discovery call. Orange County, Hudson Valley, NYC, and remote." />
            <div className="mx-auto w-full max-w-7xl px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-24">
                <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:items-start lg:gap-20">

                    {/* Left: Contact Messaging */}
                    <div className="min-w-0">

                        <div className="mx-auto mb-10 max-w-md lg:mx-0">
                            <img
                                src={Image}
                                alt="Hand holding a phone"
                                className="mx-auto h-auto w-full"
                            />
                        </div>

                        <h1 className="text-center text-4xl font-bold sm:text-5xl lg:text-left">{headingText(" Ready to Sync Up? ")}</h1>

                        <p className="mt-6 text-base leading-relaxed sm:text-lg">
                            Let’s connect your mission and priorities to a practical scope of work.
                        </p>

                        <p className="mt-6 text-base leading-relaxed sm:text-lg">
                            Whether you need to explain your programs, reach more people, or connect your systems, Sync Strategic is here to help.
                        </p>

                        <p className="mt-6 text-base leading-relaxed sm:text-lg">
                            Tell us what you want to improve. We’ll respond by the next business day.
                        </p>

                        {/* Social Links */}
                        <div className="mt-8 flex flex-wrap justify-center gap-2 lg:justify-start">
                            <a
                                href="https://www.linkedin.com/company/syncstrategicdigital"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn"
                                className="transition-transform hover:scale-110 hover:opacity-80"
                            >
                                <FaLinkedin className="h-auto w-12 p-2 text-brand-pink sm:w-14" />
                            </a>

                            <a
                                href="https://instagram.com/syncstrategic"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="transition-transform hover:scale-110 hover:opacity-80"
                            >
                                <FaInstagram className="h-auto w-12 p-2 text-brand-pink sm:w-14" />
                            </a>

                            <a
                                href="mailto:info@syncstrategicdigital.com"
                                aria-label="Email"
                                className="transition-transform hover:scale-110 hover:opacity-80"
                            >
                                <FaEnvelope className="h-auto w-12 p-2 text-brand-pink sm:w-14" />
                            </a>
                        </div>
                    </div>

                    {/* Right: Contact Form */}
                    <div className="w-full rounded-3xl border border-brand-black/10 bg-white p-6 shadow-sm sm:p-8 lg:p-10">

                        {status === "success" ? (
                            <div className="flex min-h-[500px] flex-col items-center justify-center text-center">

                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-pink text-2xl font-bold text-white">
                                    ✓
                                </div>

                                <h3 className="mt-6 text-3xl font-bold sm:text-4xl">{headingText(" Thanks for reaching out. ")}</h3>

                                <p className="mt-4 max-w-md text-base leading-relaxed opacity-70">
                                    We've received your message and will be in touch soon.
                                </p>

                            </div>
                        ) : (
                            <>
                                <div className="mb-8">
                                    <h3 className="text-3xl font-bold sm:text-4xl">{headingText(" Let’s talk. ")}</h3>

                                    <p className="mt-3 text-base leading-relaxed opacity-70">
                                        Tell us a little about what you're working on and how we can help.
                                    </p>
                                </div>

                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-6"
                                >

                                    {/* Name */}
                                    <div>
                                        <label
                                            htmlFor="name"
                                            className="mb-2 block text-sm font-semibold"
                                        >
                                            Name
                                        </label>

                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            placeholder="Your name"
                                            required
                                            autoComplete="name"
                                            className="w-full rounded-xl border border-brand-black/15 bg-gray-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-brand-black/35 focus:border-brand-pink focus:bg-white"
                                        />
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label
                                            htmlFor="email"
                                            className="mb-2 block text-sm font-semibold"
                                        >
                                            Email
                                        </label>

                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="you@organization.org"
                                            required
                                            autoComplete="email"
                                            onChange={() => {
                                                if (status === "invalid-email") {
                                                    setStatus("idle");
                                                }
                                            }}
                                            className={`w-full rounded-xl border bg-gray-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-brand-black/35 focus:bg-white ${
                                                status === "invalid-email"
                                                    ? "border-red-500 focus:border-red-500"
                                                    : "border-brand-black/15 focus:border-brand-pink"
                                            }`}
                                        />

                                        {status === "invalid-email" && (
                                            <p className="mt-2 text-sm text-red-600">
                                                Please enter a valid email address.
                                            </p>
                                        )}
                                    </div>

                                    {/* Company */}
                                    <div>
                                        <label
                                            htmlFor="company"
                                            className="mb-2 block text-sm font-semibold"
                                        >
                                            Organization
                                        </label>

                                        <input
                                            id="company"
                                            name="company"
                                            type="text"
                                            placeholder="Company or organization"
                                            autoComplete="organization"
                                            className="w-full rounded-xl border border-brand-black/15 bg-gray-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-brand-black/35 focus:border-brand-pink focus:bg-white"
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label
                                            htmlFor="phone"
                                            className="mb-2 block text-sm font-semibold"
                                        >
                                            Phone
                                        </label>

                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            placeholder="Optional"
                                            autoComplete="tel"
                                            className="w-full rounded-xl border border-brand-black/15 bg-gray-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-brand-black/35 focus:border-brand-pink focus:bg-white"
                                        />
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label
                                            htmlFor="message"
                                            className="mb-2 block text-sm font-semibold"
                                        >
                                            How can we help?
                                        </label>

                                        <textarea
                                            id="message"
                                            name="message"
                                            rows="6"
                                            placeholder="Tell us about your goals, challenges, or what you're looking to accomplish."
                                            required
                                            className="w-full resize-none rounded-xl border border-brand-black/15 bg-gray-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-brand-black/35 focus:border-brand-pink focus:bg-white"
                                        />
                                    </div>

                                    {/* Hidden Source */}
                                    <input
                                        type="hidden"
                                        name="source"
                                        value="sync-strategic-contact"
                                    />

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={status === "submitting"}
                                        className="w-full rounded-xl bg-brand-pink px-6 py-4 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {status === "submitting"
                                            ? "Sending..."
                                            : "Send Message"}
                                    </button>

                                    {/* Error */}
                                    {status === "error" && (
                                        <p className="text-center text-sm leading-relaxed text-red-600">
                                            Something went wrong while sending your message. Please try again.
                                        </p>
                                    )}

                                    <p className="text-center text-xs leading-relaxed opacity-50">
                                        We’ll only use your information to respond to your inquiry.
                                    </p>

                                </form>
                            </>
                        )}

                    </div>
                </div>
            </div>
        </section>
    );
}
